import { useEffect, useRef, useState, type ReactNode } from 'react';
import styled from 'styled-components';
import { cacheRead } from '../api/client';

const POLL_MS = 30000; // cada cuánto re-pide mientras el predicado de vivo sea verdadero

/**
 * Hook genérico de carga. Si se pasa `cacheKey` (la ruta del endpoint), funciona en modo
 * stale-while-revalidate: muestra al instante lo último cacheado y revalida por detrás,
 * actualizando solo si cambió. Sin cacheKey, es carga normal (loading hasta que llega la red).
 *
 * `pollWhile`: si se pasa y devuelve true sobre los datos actuales (ej. "hay un partido en
 * vivo"), el hook re-pide en silencio cada POLL_MS hasta que deje de cumplirse. Sin flicker.
 */
export function useAsync<T>(
  loader: () => Promise<T>,
  deps: unknown[],
  cacheKey?: string,
  pollWhile?: (data: T) => boolean,
) {
  const seed = cacheKey ? cacheRead<T>(cacheKey) : null;
  const [data, setData] = useState<T | null>(seed);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(seed === null);

  // El loader se recrea en cada render (arrow inline); lo guardamos en ref para que el
  // intervalo de polling use siempre la última versión sin reiniciarse en cada render.
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    let alive = true;
    const cached = cacheKey ? cacheRead<T>(cacheKey) : null;
    if (cached !== null) setData(cached); // instantáneo
    else setLoading(true);
    setError(null);
    loader()
      .then((d) => alive && setData(d))
      .catch((e) => alive && cached === null && setError(e instanceof Error ? e.message : 'Error'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, deps);

  // Auto-refresh solo mientras el predicado se cumpla (ej. hay partido en vivo). El booleano
  // `shouldPoll` no cambia entre actualizaciones de datos consecutivas, así el intervalo no
  // se reinicia: solo arranca cuando empieza el vivo y se corta cuando termina.
  const shouldPoll = !!(pollWhile && data !== null && pollWhile(data));
  useEffect(() => {
    if (!shouldPoll) return;
    const id = setInterval(() => {
      loaderRef.current()
        .then((d) => setData(d))
        .catch(() => {/* un fallo puntual no rompe el polling */});
    }, POLL_MS);
    return () => clearInterval(id);
  }, [shouldPoll]);

  return { data, error, loading };
}

const Center = styled.div`
  padding: 48px 16px;
  text-align: center;
  color: ${({ theme }) => theme.colors.textMuted};
`;

// Renderiza children solo cuando hay datos; muestra carga/error mientras tanto.
export function Async<T>({
  state,
  children,
}: {
  state: { data: T | null; error: string | null; loading: boolean };
  children: (data: T) => ReactNode;
}) {
  if (state.loading && !state.data) return <Center>Cargando…</Center>;
  if (state.error && !state.data) return <Center>⚠️ {state.error}</Center>;
  if (!state.data) return <Center>Sin datos</Center>;
  return <>{children(state.data)}</>;
}
